import { useState, type ReactNode } from "react"
import { CaretDown, CaretUp } from "@phosphor-icons/react"

import { Badge, IconButton, StatRow } from "~/components/design-system/components"
import { SizingSpinner } from "~/components/demand-2/shared/SizingSpinner"
import { signedUnits } from "~/components/demand-2/shared/demandBreakdownShared"
import { STRATEGIC_ADJUSTMENTS_TITLE } from "~/components/demand-2/shared/adjustmentBadges"
import {
  deltaTone,
  strategySignalComponents,
  type SignalComponent,
} from "~/components/demand-2/demand/SettingsView"
import { type SetupSummary, useSetupSummaryStore } from "~/components/demand-2/demand/setupSummary"
import type { NoosMetricKey } from "~/components/demand-2/shared/noosBreakdown"
import { strategyWaterfall } from "~/components/demand-2/demand/setupWaterfall"
import SuperChart, { type WaterfallChartData } from "~/components/reusable/graphs/SuperChart"
import { ChartType } from "~/constants/graphs"
import { formatNumber } from "~/utils/formatting"
import { getTailwindValue } from "~/utils/tailwind"
import { STRATEGY_ON_HOLD_NOTE } from "~/utils/demandStrategyGate"
import { cn, roundNumber } from "~/utils/helpers"

export const STRATEGY_SECTION_KEYS = ["baseline", "insights", "assortment", "demand"] as const
export type StrategySectionKey = (typeof STRATEGY_SECTION_KEYS)[number]

type StepKey = StrategySectionKey | "model" | "otherSetup"

type Step = {
  key: StepKey
  label: string
  /** Waterfall column label. */
  short: string
  swatch: string
  /** Baseline is a level; every other step is a delta on the running total. */
  units: number
  body?: ReactNode
  /** Items in `body`; the row only expands with more than one. */
  itemCount?: number
  /** Shown under the row whether or not it is open. */
  note?: string
}

const WATERFALL_HEIGHT = 150

const unitsLabel = (units: number) => `${formatNumber(Math.round(units))} units`

/** "37,9k": the bars are too narrow for full figures; the rows below carry them. */
const compact = (units: number) => {
  const abs = Math.abs(units)
  return abs >= 1000 ? `${formatNumber(abs / 1000, 1)}k` : formatNumber(Math.round(abs))
}

const compactSigned = (units: number) => {
  const label = compact(units)
  if (Math.round(units) === 0) return label
  return `${units > 0 ? "+" : "−"}${label}`
}

const deltaColorVar = (units: number) =>
  units >= 0 ? "--color-badge-green-fg-text" : "--color-badge-red-fg-text"

const deltaTextClass = (units: number) => {
  if (Math.round(units) === 0) return "text-fg-subtle"
  return units > 0 ? "text-badge-green-fg-text" : "text-badge-red-fg-text"
}

const pointLabel = (format: string, colorVar: string) => ({
  enabled: true,
  inside: false,
  crop: false,
  overflow: "allow" as const,
  format,
  style: { color: getTailwindValue(colorVar), fontWeight: "normal", textOutline: "none" },
})

/** Baseline, each step as a floating delta, then the end level as the closing sum. */
const Waterfall = ({ steps, end }: { steps: Step[]; end: number }) => {
  const points = [
    ...steps.map((step) => {
      if (step.key === "baseline")
        return {
          name: step.short,
          y: step.units,
          color: getTailwindValue("--color-fg-muted"),
          dataLabels: pointLabel(compact(step.units), "--color-fg-subtle"),
        }
      return {
        name: step.short,
        y: step.units,
        color: getTailwindValue(deltaColorVar(step.units)),
        dataLabels: pointLabel(compactSigned(step.units), deltaColorVar(step.units)),
      }
    }),
    {
      name: "Total",
      y: 0,
      isIntermediateSum: true,
      color: getTailwindValue("--color-fg-base"),
      dataLabels: pointLabel(compact(end), "--color-fg-subtle"),
    },
  ]
  const data: WaterfallChartData[] = [{ name: "Expected demand", data: points }]
  return (
    <SuperChart
      type={ChartType.Waterfall}
      data={data}
      xCategories={points.map((p) => p.name)}
      height={WATERFALL_HEIGHT}
      noLegend
      showYAxisLabels={false}
      showTickLines={false}
      margin={[20]}
    />
  )
}

/** Same disclosure pattern as the insight rows: no approved primitive is a clickable row. */
const NavItem = ({ onClick, children }: { onClick?: () => void; children: ReactNode }) => {
  if (!onClick) return children
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== " ") return
        e.preventDefault()
        onClick()
      }}
      className="-mx-1.5 flex cursor-pointer flex-col gap-1.5 rounded-md px-1.5 py-0.5 hover:bg-component-hover"
    >
      {children}
    </div>
  )
}

const ComponentRows = ({
  components,
  onSelect,
}: {
  components: SignalComponent[]
  /** Absent: the item has no place on the page to go to. */
  onSelect?: (key: string) => void
}) =>
  components.map((c) => (
    <NavItem key={c.key} onClick={onSelect && (() => onSelect(c.key))}>
      <StatRow
        label={c.label}
        value={c.units == null ? "—" : signedUnits(c.units)}
        tone={deltaTone(c.units)}
      />
    </NavItem>
  ))

const AccordionRow = ({
  step,
  open,
  onToggle,
  onNavigate,
}: {
  step: Step
  open: boolean
  onToggle: () => void
  /** Goes to the step's section; absent on a step with no section. */
  onNavigate?: () => void
}) => {
  const expandable = step.body != null && (step.itemCount ?? 0) > 1
  const onActivate = onNavigate ?? (expandable ? onToggle : undefined)
  const Caret = open ? CaretUp : CaretDown
  const value =
    step.key === "baseline" ? formatNumber(Math.round(step.units)) : signedUnits(step.units)
  return (
    <div className={cn("border-t border-base", open && expandable && "bg-subtle")}>
      <div
        className={cn(
          "flex items-center gap-1 pr-2",
          onActivate && "hover:bg-subtle",
          !expandable && "pr-9",
        )}
      >
        {/* Same disclosure pattern as the insight rows: no approved primitive is a full-width row. */}
        <div
          role={onActivate ? "button" : undefined}
          tabIndex={onActivate ? 0 : undefined}
          onClick={onActivate}
          onKeyDown={(e) => {
            if (!onActivate || (e.key !== "Enter" && e.key !== " ")) return
            e.preventDefault()
            onActivate()
          }}
          className={cn(
            "flex min-w-0 flex-1 items-center gap-2 py-2.5 pl-4",
            onActivate && "cursor-pointer",
          )}
        >
          <span className={cn("size-2 shrink-0 rounded-sm", step.swatch)} />
          <span className="label-sm min-w-0 flex-1 truncate text-left text-fg-base">
            {step.label}
          </span>
          <span
            className={cn(
              "label-sm tabular-nums",
              step.key === "baseline" ? "text-fg-base" : deltaTextClass(step.units),
            )}
          >
            {value}
          </span>
        </div>
        {expandable && (
          <IconButton
            size="xs"
            variant="transparent"
            aria-label={open ? `Collapse ${step.label}` : `Expand ${step.label}`}
            aria-expanded={open}
            onClick={onToggle}
            className="shrink-0"
          >
            <Caret className="size-3 text-fg-subtle" />
          </IconButton>
        )}
      </div>
      {step.note && (
        <div className="-mt-1.5 px-4 pb-2.5 pl-8">
          <Note>{step.note}</Note>
        </div>
      )}
      {open && expandable && (
        <div className="flex flex-col gap-1.5 px-4 pb-3 pl-8">{step.body}</div>
      )}
    </div>
  )
}

const Note = ({ children }: { children: ReactNode }) => (
  <span className="paragraph-xs pt-1 text-fg-subtle">{children}</span>
)

/**
 * The one summary beside every section: the Demand signals stage broken down by the section that
 * moves it. Manual forecast and Campaigns come after this stage and stay out. A row click scrolls to
 * its section; a row with several items has a caret that lists them, each scrolling to where it is
 * set on the page.
 */
export const StrategySummaryRail = ({
  summary,
  onNavigate,
  kind,
  strategyBlocked = false,
}: {
  summary: SetupSummary
  /** Makes `section` the one in view and scrolls to `target` (an insight key) or the section. */
  onNavigate: (section: StrategySectionKey, target?: string) => void
  kind: "fashion" | "noos"
  strategyBlocked?: boolean
}) => {
  const [openKey, setOpenKey] = useState<StepKey | null>(null)
  const toSection = (section: StrategySectionKey) => () => onNavigate(section)
  // Opens the row as clicking it on the page would.
  const focusInsight = (key: string, section: StrategySectionKey) => {
    useSetupSummaryStore.getState().setExpanded(section, key as NoosMetricKey, true)
    onNavigate(section, key)
  }
  const isSection = (key: StepKey): key is StrategySectionKey =>
    (STRATEGY_SECTION_KEYS as readonly string[]).includes(key)

  const { stages, sizing, levelName, appliedElsewhere } = summary
  const w = strategyWaterfall(summary)
  const parts = strategySignalComponents(summary)
  const fashion = kind === "fashion"
  const vsBaseline = w.end - w.baseline
  const pct = w.baseline === 0 ? null : roundNumber((vsBaseline / w.baseline) * 100, 0)
  const sign = vsBaseline > 0 ? "+" : "−"
  const strategyNote = strategyBlocked ? STRATEGY_ON_HOLD_NOTE : undefined

  const steps: Step[] = [
    {
      key: "baseline",
      label: "Baseline",
      short: "Baseline",
      swatch: "bg-fg-muted",
      units: w.baseline,
    },
    ...(Math.round(w.modelMove) !== 0
      ? [
          {
            key: "model" as const,
            label: "Model forecast",
            short: "Model",
            swatch: "bg-fg-subtle",
            units: w.modelMove,
          },
        ]
      : []),
    {
      key: "insights",
      label: "Insights",
      short: "Insights",
      swatch: "bg-badge-blue-fg-text",
      units: w.insights,
      itemCount: parts.insights.length,
      body: (
        <>
          <ComponentRows
            components={parts.insights}
            onSelect={(key) => focusInsight(key, "insights")}
          />
          {parts.insights.length === 0 && <Note>{`No insights move this ${levelName} yet.`}</Note>}
          {appliedElsewhere.length > 0 && (
            <Note>
              {`${appliedElsewhere.map((r) => r.label).join(", ")} ${appliedElsewhere.length === 1 ? "is" : "are"} in this total from the variant's own data or a decision made elsewhere.`}
            </Note>
          )}
        </>
      ),
    },
    {
      key: "assortment",
      label: "Assortment strategy",
      short: "Assortment",
      swatch: "bg-badge-violet-fg-text",
      units: w.assortment,
      itemCount: 1,
      note: strategyNote,
      body: (
        <>
          <NavItem onClick={toSection("assortment")}>
            <StatRow
              label="Cannibalization"
              value={signedUnits(w.assortment)}
              tone={deltaTone(w.assortment)}
            />
          </NavItem>
        </>
      ),
    },
    ...(fashion
      ? [
          {
            key: "demand" as const,
            label: "Demand strategy",
            short: "Demand",
            swatch: "bg-badge-green-fg-text",
            units: w.demand,
            itemCount: parts.demand.length,
            note: strategyNote,
            body: (
              <>
                <ComponentRows
                  components={parts.demand}
                  onSelect={(key) => focusInsight(key, "demand")}
                />
                {parts.demand.length === 0 && (
                  <Note>Growth moves the plan only once it is answered.</Note>
                )}
              </>
            ),
          },
        ]
      : []),
    ...(Math.round(w.otherSetup) !== 0
      ? [
          {
            key: "otherSetup" as const,
            label: STRATEGIC_ADJUSTMENTS_TITLE,
            short: "Adjustments",
            swatch: "bg-fg-interactive",
            units: w.otherSetup,
            itemCount: parts.otherSetup.length,
            body: <ComponentRows components={parts.otherSetup} />,
          },
        ]
      : []),
  ]

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-base bg-base shadow-card-rest">
      <div className="flex flex-col gap-3 p-4">
        <div className="flex flex-col gap-1">
          <span className="paragraph-sm text-fg-subtle">Expected demand</span>
          <div className="flex items-baseline gap-1.5">
            <span className="heading-lg tabular-nums text-fg-base">
              {sizing ? "—" : formatNumber(Math.round(w.end))}
            </span>
            <span className="paragraph-sm text-fg-subtle">units</span>
          </div>
          {!sizing && Math.round(vsBaseline) !== 0 && (
            <Badge variant={vsBaseline > 0 ? "green" : "red"} size="sm" className="self-start">
              {`${sign}${formatNumber(Math.abs(Math.round(vsBaseline)))} vs baseline${pct == null ? "" : ` · ${sign}${Math.abs(pct)}%`}`}
            </Badge>
          )}
        </div>
        {sizing ? (
          <SizingSpinner className="h-24 w-full" />
        ) : (
          <Waterfall steps={steps} end={w.end} />
        )}
      </div>
      {!sizing &&
        steps.map((step) => (
          <AccordionRow
            key={step.key}
            step={step}
            open={openKey === step.key}
            onToggle={() => setOpenKey(openKey === step.key ? null : step.key)}
            onNavigate={
              isSection(step.key) ? () => onNavigate(step.key as StrategySectionKey) : undefined
            }
          />
        ))}
      {!sizing && (
        <div className="flex flex-col gap-1 border-t border-base bg-subtle px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <span className="label-sm text-fg-base">Signal based demand</span>
            <span className="label-sm tabular-nums text-fg-base">{unitsLabel(w.end)}</span>
          </div>
          {Math.round(stages.total - w.end) !== 0 && (
            <Note>
              {`Manual forecast and campaigns come on top: Total demand is ${unitsLabel(stages.total)}.`}
            </Note>
          )}
        </div>
      )}
    </div>
  )
}
